/**
 * The wallpaper behind a scene, and the fade from one to the next.
 *
 * Two layers live in index.html and neither is ever created or destroyed.
 * Switching scene puts the new picture on whichever layer is idle, waits for
 * it to decode, and then swaps the two opacities - so the old wallpaper
 * stays on screen until the new one is genuinely ready. The window never
 * flashes black, and it never shows the default picture on the way.
 *
 * None of this is React: the boot script in index.html fills the first layer
 * before any module loads, and this file picks up from whatever it left. A
 * component that wants to know which scene is showing subscribes.
 */

import { isScene, scene, type SceneId } from './themes';

/* The timeline from the brief. The UI colours are already crossfading when
   this starts, so the wallpaper begins a beat late and finishes last. */
const WALL_DELAY = 50;
const PARTICLES_IN = 130;
const CLEANUP = 470;

type Layer = 'a' | 'b';

let showing: SceneId | null = null;
let active: Layer = 'a';
let token = 0;
const timers: number[] = [];

/* What the ambient layer is currently allowed to draw. Null between the
   moment a switch starts and the moment the new wallpaper is in. */
let drawing: SceneId | null = null;
const watchers = new Set<() => void>();

function root() { return document.documentElement; }

function notify(next: SceneId | null) {
  if (drawing === next) return;
  drawing = next;
  for (const w of watchers) w();
}

function clearTimers() {
  for (const t of timers) clearTimeout(t);
  timers.length = 0;
}

function later(fn: () => void, ms: number) {
  timers.push(window.setTimeout(fn, ms));
}

/*
 * A hint, not a cache.
 *
 * Ten short strings at most, and never the pictures themselves - those
 * belong to the engine's own image cache, which is free to throw them away
 * whenever it likes. The obvious next change is to hold the Image objects
 * here, and that is how an app ends up keeping sixty megabytes of wallpaper
 * nobody is looking at.
 */
const loaded = new Set<string>();

/**
 * Fetch and decode a wallpaper.
 *
 * Resolves false rather than throwing when the picture is missing: a scene
 * whose artwork failed to ship should still colour the app, it just keeps
 * whatever wallpaper is already on screen.
 */
export function preloadScene(id: string): Promise<boolean> {
  const s = scene(id);
  if (!s) return Promise.resolve(false);
  const src = s.backgroundImage;
  if (loaded.has(src)) return Promise.resolve(true);
  return new Promise<boolean>((resolve) => {
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => { loaded.add(src); resolve(true); };
    img.onerror = () => {
      console.warn('[scenes] wallpaper failed to load, keeping the current one:', src);
      resolve(false);
    };
    img.src = src;
  });
}

/**
 * Show this theme's wallpaper, or none at all for the flat themes.
 *
 * Called from applyPrefs, so it runs on every settings save - including the
 * ones that have nothing to do with the theme. It returns immediately unless
 * the scene actually changed.
 */
export function applyScene(themeId: string): void {
  /* On the very first call the boot script has already put a scene on the
     screen. Believe the DOM rather than starting from nothing, or the first
     settings save fades the wallpaper it just painted. */
  if (showing === null) {
    const painted = root().dataset.scene;
    if (isScene(painted)) { showing = painted; drawing = painted; }
  }

  const next = isScene(themeId) ? themeId : null;
  if (next === showing) return;

  clearTimers();
  const mine = ++token;
  showing = next;

  const el = root();
  if (!next) {
    /* Back to a flat theme: the layers fade out together and the app's own
       background, which was behind them all along, is what is left. */
    notify(null);
    el.style.setProperty('--pugo-wall-a-on', '0');
    el.style.setProperty('--pugo-wall-b-on', '0');
    el.style.setProperty('--pugo-bloom-on', '0');
    delete el.dataset.scene;
    delete el.dataset.bloom;
    later(() => {
      if (token !== mine) return;
      el.style.setProperty('--pugo-wall-a', 'none');
      el.style.setProperty('--pugo-wall-b', 'none');
    }, CLEANUP);
    return;
  }

  const s = scene(next)!;

  /* T = 0. The palette is already crossfading - applyPrefs set data-theme
     before calling - so the scene's own layers follow at once, and anything
     drifting past the old wallpaper fades down. */
  el.dataset.scene = s.id;
  if (s.effects.bloomPulse) el.dataset.bloom = 'pulse'; else delete el.dataset.bloom;
  el.style.setProperty('--pugo-bloom-on', '1');
  notify(null);

  void preloadScene(s.id).then((ok) => {
    if (token !== mine) return;
    if (!ok) {
      /* The colours have already changed and the old picture is still up.
         That is the graceful end of this: bring the particles back and say
         nothing else about it. */
      notify(s.id);
      return;
    }

    const idle: Layer = active === 'a' ? 'b' : 'a';
    el.style.setProperty('--pugo-wall-' + idle, 'url("' + s.backgroundImage + '")');

    later(() => {
      if (token !== mine) return;
      el.style.setProperty('--pugo-wall-' + idle + '-on', '1');
      el.style.setProperty('--pugo-wall-' + active + '-on', '0');
      const old = active;
      active = idle;
      /* Let go of the picture nobody can see any more. */
      later(() => {
        if (token !== mine) return;
        el.style.setProperty('--pugo-wall-' + old, 'none');
      }, CLEANUP - WALL_DELAY);
    }, WALL_DELAY);

    later(() => {
      if (token !== mine) return;
      notify(s.id);
    }, PARTICLES_IN);
  });
}

/* ------------------------------------------------------- for components */

/** The scene the ambient layer should be drawing, if any. */
export function drawingScene(): SceneId | null {
  return drawing;
}

export function watchScene(cb: () => void): () => void {
  watchers.add(cb);
  return () => { watchers.delete(cb); };
}
